/**
 * The employee's own day, hour by hour, from the rollup — spec 01 §17's
 * "active/idle timeline từ aggregate".
 *
 * `GET /activities/timeline?source=aggregate&granularity=hour`. Aggregate first, per
 * 02 §14; the segment drill-down is a different question at a different resolution
 * and is not asked here.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  FIVE KINDS OF ABSENCE, AND NOT ONE OF THEM IS A ZERO
 *
 *  `src/modules/aggregates/service.ts` writes one row per **elapsed** local hour —
 *  `bucketsForLocalDay(...).filter(startMs < notAfterMs)` — including hours in
 *  which nothing happened. So "there is no row" means one of four different things,
 *  and `openapi.yaml` says the fifth in as many words:
 *
 *   1. **The hour has not happened.** `bucket_start >= now`. Not rendered at all;
 *      the timeline stops at the current hour. An unstarted hour drawn as an empty
 *      bar is the most easily believed zero on this screen.
 *   2. **The hour did not exist.** The spring-forward day skips one on the local
 *      clock. Also not rendered — see the guard in `hourRowsFor`.
 *   3. **Not computed, and there is data waiting.** No row, and this date is in
 *      `coverage.not_computed_days` — which lists (employee, date) pairs that have
 *      segments and no aggregate row. "Chưa tính", plus `DataDelayed`
 *      `source='processing'` on the region: the sweep is behind, and the numbers on
 *      screen are a floor rather than a total.
 *   4. **Not computed, and nothing is waiting.** No row and the date is not listed.
 *      "Không có dữ liệu cho giờ này" — and the sentence that this is not a claim
 *      about whether the person worked.
 *   5. **Computed, nothing observed.** A row with `completeness_status: 'missing'`.
 *      `openapi.yaml`: "*not* the same claim as 'the employee did nothing'". Its own
 *      words, its own icon, and no number.
 *
 *  Cases 3, 4 and 5 all look identical if you render zeros. They are three
 *  different people's problems. Cases 1 and 2 look identical to them too, and are
 *  nobody's problem at all — which is why they are not drawn.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * ── Private activity is a band, not a gap ──────────────────────────────────
 *
 * `private_seconds` is time that **was worked, with no name** (Task 35), counted by
 * Task 36 in `online` and `active` and in no category column at all. It is drawn in
 * the bar at the same weight as named activity, labelled "Riêng tư — đã tính là thời
 * gian làm việc", and carries no correction affordance: offering one would frame an
 * exercised privacy right as a discrepancy. See `web/shapes/my-activity.md` §4.2.
 */

import type { ReactNode } from 'react';

import { useI18n, type Translate } from '../../i18n';
import { formatDay, wallClockToInstant, zoneOffsetMsAt } from '../../i18n/datetime';
import { timelineHoursPath } from '../../api/paths';
import type { TimelineBucket, TimelineBucketListResponse } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { Badge } from '../../states/Badge';
import { DataDelayed } from '../../states/DataDelayed';
import { Skeleton } from '../../states/Skeleton';
import { formatDurationSeconds } from '../../timer/elapsed';

export interface HourTimelineProps {
  readonly date: string;
  readonly timezone: string;
  readonly employeeId: string;
  /** Injected by tests so "which hours have happened" is a value and not a race. */
  readonly nowMs?: number;
}

/** What a rendered row is. The two absences that are not drawn have no member here. */
type HourKind = 'complete' | 'partial' | 'missing' | 'not-computed' | 'no-data';

interface HourRow {
  readonly hour: number;
  readonly kind: HourKind;
  readonly bucket: TimelineBucket | null;
}

const KIND_BADGE: Readonly<
  Record<
    HourKind,
    {
      severity: 'low' | 'medium' | 'info';
      icon: 'circle-check' | 'circle-alert' | 'hourglass' | 'info';
      labelKey: Parameters<Translate>[0];
      helpKey: Parameters<Translate>[0];
    }
  >
> = {
  complete: {
    severity: 'low',
    icon: 'circle-check',
    labelKey: 'timeline.kind.complete',
    helpKey: 'timeline.kind.complete.help',
  },
  partial: {
    severity: 'medium',
    icon: 'circle-alert',
    labelKey: 'timeline.kind.partial',
    helpKey: 'timeline.kind.partial.help',
  },
  missing: {
    severity: 'info',
    icon: 'info',
    labelKey: 'timeline.kind.missing',
    helpKey: 'timeline.kind.missing.help',
  },
  'not-computed': {
    severity: 'info',
    icon: 'hourglass',
    labelKey: 'timeline.kind.notComputed',
    helpKey: 'timeline.kind.notComputed.help',
  },
  'no-data': {
    severity: 'info',
    icon: 'info',
    labelKey: 'timeline.kind.noData',
    helpKey: 'timeline.kind.noData.help',
  },
};

/**
 * The hours of `date` that have already begun, each classified.
 *
 * Exported so a test can drive the classification without a DOM: deciding which of
 * the five absences in the header a given hour is — and which two are not drawn at
 * all — is the load-bearing logic on this screen, and it should be assertable as
 * data rather than only as pixels.
 */
export function hourRowsFor(input: {
  readonly date: string;
  readonly timezone: string;
  readonly employeeId: string;
  readonly nowMs: number;
  readonly buckets: readonly TimelineBucket[];
  readonly notComputed: readonly { readonly employee_id: string; readonly session_date: string }[];
}): readonly HourRow[] {
  const byHour = new Map<number, TimelineBucket>();
  for (const bucket of input.buckets) {
    if (bucket.hour_local === undefined) continue;
    // On the autumn transition day two rows share an `hour_local`; the first wins
    // and the second is not silently added to it. Summing them would invent an hour
    // that did not exist and would contradict `active + idle = online`.
    if (!byHour.has(bucket.hour_local)) byHour.set(bucket.hour_local, bucket);
  }

  const dateIsWaiting = input.notComputed.some(
    (pair) => pair.session_date === input.date && pair.employee_id === input.employeeId,
  );

  const rows: HourRow[] = [];
  for (let hour = 0; hour < 24; hour += 1) {
    const wall = `${input.date}T${String(hour).padStart(2, '0')}:00`;
    const startsAt = wallClockToInstant(wall, input.timezone);
    if (startsAt === null) continue;

    // **An hour that does not exist is not an hour with no data.** On a
    // spring-forward day the local clock skips one, and `wallClockToInstant` — like
    // `zonedTimeToInstant` on the server — resolves the missing wall time to the
    // instant after the jump rather than throwing. Reading the local hour back is
    // what tells the two apart: if the instant does not land on the hour that was
    // asked for, that hour was never on anybody's clock, the rollup has no bucket
    // keyed on it, and rendering it would invent a gap out of a clock change. This
    // is the same discipline as the four cases in the header, one level down: three
    // of them are about an hour that happened, and this is the fourth kind of
    // absence, which is not one of them.
    const localHour = Math.floor(
      ((((Date.parse(startsAt) + zoneOffsetMsAt(input.timezone, Date.parse(startsAt))) %
        86_400_000) +
        86_400_000) %
        86_400_000) /
        3_600_000,
    );
    if (localHour !== hour) continue;

    if (Date.parse(startsAt) >= input.nowMs) continue;

    const bucket = byHour.get(hour) ?? null;
    const kind: HourKind =
      bucket !== null ? bucket.completeness_status : dateIsWaiting ? 'not-computed' : 'no-data';
    rows.push({ hour, kind, bucket });
  }
  return rows;
}

function Bar({ bucket }: { readonly bucket: TimelineBucket | null }): ReactNode {
  if (bucket === null) return <span className="wp-bar wp-bar--empty" aria-hidden="true" />;
  const total = bucket.online_seconds > 0 ? bucket.online_seconds : 3600;
  const priv = bucket.private_seconds ?? 0;
  // Private seconds are counted inside `active`, so drawing both at full width would
  // double the hour. The named part is what is left after the private part.
  const named = Math.max(bucket.active_seconds - priv, 0);
  const pct = (seconds: number): string => `${String(Math.round((seconds / total) * 100))}%`;
  return (
    <span className="wp-bar" aria-hidden="true">
      <span className="wp-bar__seg wp-bar__seg--active" style={{ width: pct(named) }} />
      <span className="wp-bar__seg wp-bar__seg--private" style={{ width: pct(priv) }} />
      <span className="wp-bar__seg wp-bar__seg--idle" style={{ width: pct(bucket.idle_seconds) }} />
    </span>
  );
}

export function HourTimeline({ date, timezone, employeeId, nowMs }: HourTimelineProps): ReactNode {
  const { t, locale } = useI18n();
  const fromIso = wallClockToInstant(`${date}T00:00`, timezone);
  const toIso = wallClockToInstant(`${date}T00:00`, timezone) ?? '';
  const nextDay = new Date(Date.parse(toIso === '' ? `${date}T00:00:00Z` : toIso) + 86_400_000);
  const path = fromIso === null ? null : timelineHoursPath(fromIso, nextDay.toISOString());
  const { status, data, error, reload } = useApiQuery<TimelineBucketListResponse>(path);

  const durations = { hour: t('common.hourShort'), minute: t('common.minuteShort') };
  const coverage = data?.coverage ?? null;
  const rows =
    data === null
      ? []
      : hourRowsFor({
          date,
          timezone,
          employeeId,
          nowMs: nowMs ?? Date.now(),
          buckets: data.data,
          notComputed: coverage?.not_computed_days ?? [],
        });
  /** Whether any hour of this day has a rollup row at all. Drives both the empty
      state and whether the hour rows start open. */
  const hasAnyData = rows.some((row) => row.bucket !== null);

  return (
    <section className="wp-section" aria-labelledby="timeline-heading">
      <h2 className="wp-section__title" id="timeline-heading">
        {t('timeline.heading')}
      </h2>
      <p className="wp-small wp-muted">{t('timeline.intro')}</p>

      {status === 'loading' ? (
        <Skeleton rows={6} label={t('timeline.loading')} testId="timeline-skeleton" />
      ) : null}

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('timeline.subject')}
          testId="timeline-error"
        />
      ) : null}

      {coverage !== null && !coverage.complete ? (
        <DataDelayed
          source="processing"
          since={null}
          timezone={timezone}
          detail={t('timeline.notCaughtUp')}
          testId="timeline-not-caught-up"
        />
      ) : null}

      {/*
        Twenty-four rows, each with a badge reading "KHÔNG CÓ DỮ LIỆU" and the
        sentence "Không có dữ liệu" under it, is a page that says nothing 24 times.
        One empty state says it once (finish review, fix 5) — and the rows stay,
        behind a collapsible group header, because on a day that *does* have data
        the hour-by-hour shape is the whole point of the screen. The `<details>` is
        open exactly when there is something in it to look at.
      */}
      {data !== null && rows.length > 0 && !hasAnyData ? (
        <EmptyState
          title={t('timeline.empty.title')}
          description={t('timeline.empty.body')}
          testId="timeline-empty"
        />
      ) : null}

      {data === null ? null : (
        <details className="wp-disclosure wp-timeline__hours" open={hasAnyData}>
          <summary className="wp-disclosure__summary" data-testid="timeline-hours-toggle">
            {t('timeline.hours.toggle', { count: rows.length })}
          </summary>
          <div className="wp-table-scroll">
            <table
              className="wp-table wp-timeline-table wp-e-hourly"
              role="table"
              data-testid="timeline-table"
            >
              <caption className="wp-visually-hidden">
                {t('timeline.caption', {
                  date: formatDay(date, locale, { day: 'numeric', month: 'long', year: 'numeric' }),
                })}
              </caption>
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">
                    {t('timeline.col.hour')}
                  </th>
                  <th scope="col" role="columnheader">
                    {t('timeline.col.shape')}
                  </th>
                  <th scope="col" role="columnheader">
                    {t('timeline.col.detail')}
                  </th>
                  <th scope="col" role="columnheader">
                    {t('timeline.col.state')}
                  </th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {rows.map((row) => {
                  const badge = KIND_BADGE[row.kind];
                  const bucket = row.bucket;
                  const priv = bucket?.private_seconds ?? 0;
                  return (
                    <tr key={row.hour} role="row" data-testid={`timeline-hour-${String(row.hour)}`}>
                      <th scope="row" role="rowheader" className="wp-mono">
                        {String(row.hour).padStart(2, '0')}:00
                      </th>
                      <td className="wp-e-hourly__bar" role="cell">
                        <Bar bucket={bucket} />
                      </td>
                      <td className="wp-e-hourly__detail" role="cell">
                        {bucket === null ? (
                          <span className="wp-muted">{t(badge.labelKey)}</span>
                        ) : bucket.completeness_status === 'missing' ? (
                          <span className="wp-muted">{t('timeline.kind.missing.help')}</span>
                        ) : (
                          <span>
                            {t('timeline.detail', {
                              active: formatDurationSeconds(
                                Math.max(bucket.active_seconds - priv, 0),
                                durations,
                              ),
                              idle: formatDurationSeconds(bucket.idle_seconds, durations),
                            })}
                            {priv > 0 ? (
                              <>
                                {' · '}
                                <span data-testid={`timeline-private-${String(row.hour)}`}>
                                  <Badge
                                    severity="low"
                                    icon="lock"
                                    label={t('timeline.private', {
                                      duration: formatDurationSeconds(priv, durations),
                                    })}
                                    title={t('timeline.private.help')}
                                    testId={`timeline-private-badge-${String(row.hour)}`}
                                  />
                                </span>
                              </>
                            ) : null}
                          </span>
                        )}
                      </td>
                      <td className="wp-e-hourly__state" role="cell">
                        <Badge
                          severity={badge.severity}
                          icon={badge.icon}
                          label={t(badge.labelKey)}
                          title={t(badge.helpKey)}
                          testId={`timeline-state-${String(row.hour)}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {data !== null && rows.length === 0 ? (
        <p className="wp-muted" data-testid="timeline-not-started">
          {t('timeline.notStarted')}
        </p>
      ) : null}
    </section>
  );
}
